export interface PeerState {
  id: string;
  name: string;
  x: number;
  z: number;
  yaw: number;
  speed: number;
  torch: boolean;
}

export interface LocalState {
  x: number;
  z: number;
  yaw: number;
  speed: number;
  torch: boolean;
}

export interface PartyEvent {
  id: string;
  name: string;
}

export interface Session {
  readonly peers: Map<string, PeerState>;
  readonly connected: boolean;
  readonly selfId: string | null;
  update(delta: number, local: LocalState): void;
  invite(id: string): void;
  accept(id: string): void;
  consumeInvites(): PartyEvent[];
  consumeAccepts(): PartyEvent[];
  close(): void;
}

const SEND_INTERVAL = 1 / 12;

export function connectSession(world: string, name: string, host: string): Session {
  const peers = new Map<string, PeerState>();
  const lastSeen = new Map<string, number>();
  const silenceLimit = 8000;
  let socket: WebSocket | null = null;
  let connected = false;
  let retry = 0;
  let since = SEND_INTERVAL;
  let closed = false;
  let latest: LocalState | null = null;
  let selfId: string | null = null;
  let invites: PartyEvent[] = [];
  let accepts: PartyEvent[] = [];

  // an https page cannot open ws://, so the scheme follows the document
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  const url = `${scheme}//${host}/parties/main/${encodeURIComponent(world)}`;

  const open = (): void => {
    if (closed) return;
    const ws = new WebSocket(url);
    socket = ws;

    // an error is normally followed by a close, so guard against both firing and
    // opening two sockets — the second would look like a peer standing on top of us
    let settled = false;
    const drop = (): void => {
      if (settled) return;
      settled = true;
      if (socket === ws) socket = null;
      connected = false;
      peers.clear();
      lastSeen.clear();
      if (closed) return;
      retry = Math.min(retry + 1, 6);
      window.setTimeout(open, 400 * retry);
    };

    ws.addEventListener("open", () => {
      connected = true;
      retry = 0;
      ws.send(JSON.stringify({ t: "hello", name }));
      if (latest) ws.send(JSON.stringify({ t: "move", ...latest }));
    });

    ws.addEventListener("message", (event) => {
      let message: {
        t?: string;
        peer?: PeerState;
        peers?: PeerState[];
        id?: string;
        from?: string;
        self?: string;
        by?: string;
        name?: string;
      };
      try {
        message = JSON.parse(String(event.data));
      } catch {
        return;
      }

      if (message.t === "welcome" && message.id) {
        selfId = message.id;
        return;
      }
      if (message.t === "roster" && message.peers) {
        peers.clear();
        for (const peer of message.peers) {
          if (peer.id === selfId) continue;
          peers.set(peer.id, peer);
          lastSeen.set(peer.id, performance.now());
        }
        return;
      }
      if (message.peer && (message.t === "move" || message.t === "joined")) {
        if (message.peer.id === selfId) return;
        peers.set(message.peer.id, message.peer);
        lastSeen.set(message.peer.id, performance.now());
        return;
      }
      if (message.t === "left" && message.id) {
        peers.delete(message.id);
        lastSeen.delete(message.id);
      }
      if (message.t === "invited" && message.from) {
        invites.push({ id: message.from, name: message.name ?? "wanderer" });
      }
      if (message.t === "accepted" && message.by) {
        accepts.push({ id: message.by, name: message.name ?? "wanderer" });
      }
    });

    ws.addEventListener("close", drop);
    ws.addEventListener("error", drop);
  };

  open();

  return {
    peers,
    get connected(): boolean {
      return connected;
    },
    get selfId(): string | null {
      return selfId;
    },
    update(delta: number, local: LocalState): void {
      const now = performance.now();
      for (const id of peers.keys()) {
        if (now - (lastSeen.get(id) ?? now) > silenceLimit) {
          peers.delete(id);
          lastSeen.delete(id);
        }
      }

      latest = local;
      since += delta;
      if (!connected || since < SEND_INTERVAL) return;
      since = 0;
      socket?.send(JSON.stringify({ t: "move", ...local }));
    },
    invite(id: string): void {
      socket?.send(JSON.stringify({ t: "invite", to: id }));
    },
    accept(id: string): void {
      socket?.send(JSON.stringify({ t: "accept", to: id }));
    },
    consumeInvites(): PartyEvent[] {
      const value = invites;
      invites = [];
      return value;
    },
    consumeAccepts(): PartyEvent[] {
      const value = accepts;
      accepts = [];
      return value;
    },
    close(): void {
      closed = true;
      socket?.close();
    },
  };
}
